import { KeyboardEvent, useState } from 'react';

import { DateTimePicker, Icon, IconButton } from '@atom/mui';
import { EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Media } from '@atom/types/media';
import { getDisplayDateTime } from '@atom/utilities/timeUtilities';

import MediaLightboxDetailRow from './MediaLightboxDetailRow';
import MediaLightboxEditActions from './MediaLightboxEditActions';
import useMediaUpdate from './useMediaUpdate';

import classes from './mediaLightbox.module.css';

const styles = {
  whiteText: {
    color: colors.neutral.white,
  },
  editButton: {
    marginTop: '-0.3125rem',
  },
};

const toDate = (capturedDate?: number): Date =>
  capturedDate ? new Date(capturedDate) : null;

const isComplete = (date: Date): boolean =>
  !date || !Number.isNaN(date.valueOf());

export type MediaLightboxCapturedDateFieldProps = {
  media: Media;
  editable: boolean;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onSaved: () => void;
};

const MediaLightboxCapturedDateField = ({
  media,
  editable,
  editing,
  onEdit,
  onDone,
  onSaved,
}: MediaLightboxCapturedDateFieldProps) => {
  const [draft, setDraft] = useState<Date>(null);
  const { save, saving, saveError, reset } = useMediaUpdate(media.id);

  const valid = isComplete(draft);

  const handleEdit = () => {
    reset();
    setDraft(toDate(media.capturedDate));
    onEdit();
  };

  const handleSave = async () => {
    const capturedDate = draft ? draft.valueOf() : null;

    if (capturedDate === (media.capturedDate || null)) {
      onDone();
      return;
    }

    const saved = await save({ capturedDate });

    if (saved) {
      onSaved();
      onDone();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // The picker popup renders through a portal: its key events bubble here
    // via the React tree, but Enter there selects a day rather than saving.
    if (saving || !event.currentTarget.contains(event.target as Node)) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onDone();
    } else if (event.key === 'Enter' && valid) {
      event.preventDefault();
      handleSave();
    }
  };

  const readValue = (
    <div className={classes.editableValue}>
      {editable && (
        <IconButton
          size="small"
          style={styles.editButton}
          onClick={handleEdit}
          data-cy="mediaLightboxCapturedDateEditButton"
        >
          <Icon color={colors.neutral.white} icon={EditIcon} />
        </IconButton>
      )}
      <span>{getDisplayDateTime(media.capturedDate)}</span>
    </div>
  );

  const editor = (
    <div className={classes.editField} onKeyDown={handleKeyDown}>
      <DateTimePicker
        value={draft}
        onChange={date => setDraft(date)}
        autoFocus
        disabled={saving}
        error={!valid}
        helperText={valid ? '' : 'Enter a valid date and time.'}
        inputSx={{ sx: styles.whiteText }}
        data-cy="mediaLightboxCapturedDateInput"
      />
      {!!saveError && (
        <div
          className={classes.editError}
          data-cy="mediaLightboxCapturedDateError"
        >
          {saveError}
        </div>
      )}
      <MediaLightboxEditActions
        dataCy="mediaLightboxCapturedDate"
        saving={saving}
        saveDisabled={!valid}
        onCancel={onDone}
        onSave={handleSave}
      />
    </div>
  );

  return (
    <MediaLightboxDetailRow label="Captured on" stacked={editing}>
      {editing ? editor : readValue}
    </MediaLightboxDetailRow>
  );
};

export default MediaLightboxCapturedDateField;
